import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BadgeCheck, Check, ChevronRight, X } from "lucide-react";

export const useBooksStatusState = (refreshKey) => {
  const [state, setState] = useState({ rows: [], loaded: false });
  useEffect(() => {
    api.get("/books-status").then((r) => setState({ rows: r.data, loaded: true })).catch(() => setState((s) => ({ ...s, loaded: true })));
  }, [refreshKey]);
  return state;
};

export const useBooksStatus = (refreshKey) => useBooksStatusState(refreshKey).rows;

export function BooksCompleteBadge({ through, testid, large = false }) {
  return (
    <span className={`uppercase tracking-widest font-bold inline-flex items-center gap-1 ${large ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1"}`}
      style={{ background: "#EDE9FE", color: "#5B21B6", border: "1px solid #C4B5FD" }} data-testid={testid}>
      <BadgeCheck size={large ? 14 : 12} /> Books complete{through ? ` through ${through}` : ""}
    </span>
  );
}

export function BooksChecklist({ status }) {
  if (!status?.has_statements || status.complete) return null;
  const { next } = status;
  const nextUrl = next?.kind === "reconcile"
    ? `/app/reconciliation?account=${status.bank_account_id}&start=${next.start}&end=${next.end}` : next?.kind === "gap" ? "/app/statements" : null;
  return (
    <div className="px-5 py-3 border-t bg-[#FAFAF9] flex flex-wrap items-start justify-between gap-3" style={{ borderColor: "var(--ll-border)" }} data-testid={`books-checklist-${status.bank_account_id}`}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-1.5">Left before “Books complete”</div>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
          {status.items.map((i) => (
            <li key={i.key} className="text-xs flex items-start gap-1.5" data-testid={`books-item-${status.bank_account_id}-${i.key}`} data-ok={i.ok}>
              {i.ok ? <Check size={13} className="text-[#059669] mt-px shrink-0" /> : <X size={13} className="text-[#DC2626] mt-px shrink-0" />}
              <span className={i.ok ? "text-[var(--ll-ink-soft)]" : ""}>
                {i.label}
                {!i.ok && i.months?.length > 0 && <span className="text-[var(--ll-ink-muted)]"> · {i.months.slice(0, 4).join(", ")}{i.months.length > 4 ? "…" : ""}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {nextUrl && (
        <Link to={nextUrl} className="text-xs px-3 py-1.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1 whitespace-nowrap transition-colors"
          style={{ borderColor: "var(--ll-border)" }} data-testid={`books-next-${status.bank_account_id}`}>
          {next.label} <ChevronRight size={12} />
        </Link>
      )}
    </div>
  );
}

export function ClientBooksComplete() {
  const rows = useBooksStatus(0);
  if (!rows.length) return null;
  return (
    <div className="mb-6 flex flex-wrap gap-2 items-center" data-testid="client-books-complete">
      {rows.map((r) => (
        <span key={r.bank_account_id} className="inline-flex items-center gap-2 text-sm" data-testid={`client-books-${r.bank_account_id}`}>
          <BooksCompleteBadge through={r.through_label} large testid={`client-books-badge-${r.bank_account_id}`} />
          <span className="text-[var(--ll-ink-soft)] text-xs">{r.label}</span>
        </span>
      ))}
    </div>
  );
}
